import React from 'react';
import { Box, Typography, Button, Paper, Alert, Divider } from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatDate } from '../../utils/date';
import { formatNumber } from '../../utils/currency';

// Written by deploy/linux/publish-apk.sh and served by nginx next to the APK.
const ANDROID_DIR = '/downloads/android';

interface AndroidRelease {
  version: string;
  /** Android versionCode; the app updates itself when this is newer than its own. */
  versionCode?: number | null;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  publishedAt: string;
}

function formatSize(bytes: number) {
  return `${formatNumber(bytes / (1024 * 1024), 1)} MB`;
}

const MobileAppPage: React.FC = () => {
  const [release, setRelease] = React.useState<AndroidRelease | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${ANDROID_DIR}/latest.json`, { cache: 'no-store' });
        // The dev server answers unknown paths with index.html, so check the type too.
        if (!res.ok || !res.headers.get('content-type')?.includes('json')) throw new Error('No release');
        const data = (await res.json()) as AndroidRelease;
        if (!data.fileName) throw new Error('Invalid release');
        if (!cancelled) setRelease(data);
      } catch {
        if (!cancelled) setRelease(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box maxWidth={480} mx="auto">
      <Paper sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <AndroidIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h6">Home System para Android</Typography>
        </Box>

        {loading && <LoadingSpinner />}

        {!loading && !release && (
          <Alert severity="info">A app Android ainda não está disponível para descarregar.</Alert>
        )}

        {!loading && release && (
          <>
            <Typography variant="body2" color="text.secondary" align="center" mb={2}>
              Versão {release.version}
              {release.versionCode ? ` (build ${release.versionCode})` : ''} · {formatSize(release.sizeBytes)} · publicada a{' '}
              {formatDate(release.publishedAt)}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<DownloadIcon />}
              href={`${ANDROID_DIR}/${encodeURIComponent(release.fileName)}`}
              download={release.fileName}
            >
              Descarregar APK
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>Como instalar</Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
              <li>Abra esta página no telemóvel Android e toque em <b>Descarregar APK</b>.</li>
              <li>Abra o ficheiro descarregado. Se o Android pedir, deixe o navegador instalar apps desconhecidas.</li>
              <li>Confirme a instalação.</li>
              <li>
                As atualizações chegam pela própria app: propõe cada versão nova quando abre, ou veja em{' '}
                <b>Mais → Versão da app</b>. Na primeira vez, o Android pede para deixar o Home System instalar apps.
              </li>
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2, wordBreak: 'break-all' }}>
              SHA-256: {release.sha256}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MobileAppPage;
